import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  EmptyTransactions,
  EmptyFilteredTransactions,
} from "@/components/ui/empty-state-presets";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import {
  Plus,
  Search,
  Download,
  Upload,
  Pencil,
  Trash2,
  Building2,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  Filter,
  X,
  Calendar,
  DollarSign,
  Tag,
  MoreVertical,
} from "lucide-react";
import { useState, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { toast } from "@/lib/toast";
import { useIsMobile } from "@/hooks/useMobile";
import {
  MobileTableCard,
  MobileTableCardHeader,
  MobileTableCardRow,
  MobileTableCardActions,
} from "@/components/MobileTableCard";
import DashboardLayout from "@/components/DashboardLayout";
import {
  ColumnCustomizer,
  useColumnCustomization,
  type ColumnConfig,
} from "@/components/ui/column-customizer";
import {
  TableSkeleton,
  MobileTableCardSkeleton,
} from "@/components/ui/table-skeleton";

type TransactionFormData = {
  id?: number;
  companyId: number;
  date: string;
  description: string;
  amount: string;
  currency: string;
  type: "debit" | "credit" | "transfer";
  category: string;
  accountCode: string;
  accountName: string;
  vatRate?: string;
  vatAmount?: string;
  reference?: string;
  notes?: string;
  vendorId?: number;
  customerId?: number;
};

type SortField = "date" | "description" | "amount" | "category" | "status";
type SortOrder = "asc" | "desc";

export default function Transactions() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { selectedCompanyId } = useCompany();
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Advanced filters
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>();
  const [amountMin, setAmountMin] = useState<string>("");
  const [amountMax, setAmountMax] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState<string>("");
  const [bulkStatus, setBulkStatus] = useState<string>("");

  // Column customization
  const defaultColumns = [
    { id: "date", label: "Date", locked: true },
    { id: "description", label: "Description", locked: true },
    { id: "account", label: "Account" },
    { id: "category", label: "Category" },
    { id: "amount", label: "Amount", locked: true },
    { id: "status", label: "Status" },
    { id: "reference", label: "Reference" },
    { id: "type", label: "Type" },
  ];
  const { columns, setColumns, isVisible } = useColumnCustomization(
    "transactions-table",
    defaultColumns,
    ["date", "description", "account", "category", "amount", "status"]
  );

  const [formData, setFormData] = useState<TransactionFormData>({
    companyId: 1,
    date: new Date().toISOString().split("T")[0],
    description: "",
    amount: "",
    currency: "CHF",
    type: "debit",
    category: "",
    accountCode: "",
    accountName: "",
    vatRate: "",
    vatAmount: "",
    reference: "",
    notes: "",
    vendorId: undefined,
    customerId: undefined,
  });

  // Fetch companies
  const { data: companies = [] } = trpc.company.list.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Fetch transactions
  const {
    data: transactions = [],
    refetch,
    isLoading: isLoadingTransactions,
  } = trpc.transaction.list.useQuery(
    {
      companyId: selectedCompanyId || 0,
    },
    {
      enabled: isAuthenticated && !!selectedCompanyId,
    }
  );

  // Fetch vendors for expense transactions
  const { data: vendorsData } = trpc.vendorIntelligence.listVendors.useQuery(
    {
      companyId: selectedCompanyId || 0,
      limit: 100,
    },
    {
      enabled: isAuthenticated && !!selectedCompanyId,
    }
  );
  const vendors = vendorsData?.items ?? [];

  // Fetch customers for revenue transactions
  const { data: customersData } = trpc.customers.listCustomers.useQuery(
    {
      companyId: selectedCompanyId || 0,
      limit: 100,
    },
    {
      enabled: isAuthenticated && !!selectedCompanyId,
    }
  );
  const customers = customersData?.items ?? [];

  // Mutations
  const createMutation = trpc.transaction.create.useMutation({
    onSuccess: () => {
      toast.created("Transaction", {});
      refetch();
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: error => {
      toast.error(`Failed to create transaction: ${error.message}`);
    },
  });

  const updateMutation = trpc.transaction.update.useMutation({
    onSuccess: () => {
      toast.success("Transaction updated");
      refetch();
      setIsEditDialogOpen(false);
      setSelectedTransaction(null);
    },
    onError: error => {
      toast.error(`Failed to update transaction: ${error.message}`);
    },
  });

  // Store last deleted transaction for undo
  const [lastDeletedTransaction, setLastDeletedTransaction] =
    useState<typeof selectedTransaction>(null);

  const deleteMutation = trpc.transaction.delete.useMutation({
    onSuccess: () => {
      const deletedTxn = selectedTransaction;
      setLastDeletedTransaction(deletedTxn);
      toast.deleted("Transaction", {
        onUndo: () => {
          if (deletedTxn) {
            // Restore by creating a new transaction with the same data
            createMutation.mutate({
              companyId: deletedTxn.companyId,
              date: deletedTxn.date,
              description: deletedTxn.description,
              amount: deletedTxn.amount,
              currency: deletedTxn.currency,
              type: deletedTxn.type as "debit" | "credit" | "transfer",
              category: deletedTxn.category,
              accountCode: deletedTxn.accountCode,
              accountName: deletedTxn.accountName,
              reference: deletedTxn.reference || undefined,
              notes: deletedTxn.notes || undefined,
              vendorId: deletedTxn.vendorId || undefined,
              customerId: deletedTxn.customerId || undefined,
            });
            toast.success("Transaction restored");
          }
        },
      });
      refetch();
      setIsDeleteDialogOpen(false);
      setSelectedTransaction(null);
    },
    onError: error => {
      toast.error(`Failed to delete transaction: ${error.message}`);
    },
  });

  // Filter and sort transactions
  const filteredAndSortedTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        t =>
          t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.accountCode.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    // Date range filter
    if (dateRange?.from) {
      filtered = filtered.filter(t => {
        const txnDate = new Date(t.date);
        const fromDate = new Date(dateRange.from!);
        fromDate.setHours(0, 0, 0, 0);
        return txnDate >= fromDate;
      });
    }
    if (dateRange?.to) {
      filtered = filtered.filter(t => {
        const txnDate = new Date(t.date);
        const toDate = new Date(dateRange.to!);
        toDate.setHours(23, 59, 59, 999);
        return txnDate <= toDate;
      });
    }

    // Amount range filter
    if (amountMin) {
      const minInCents = parseFloat(amountMin) * 100;
      filtered = filtered.filter(t => Math.abs(t.amount) >= minInCents);
    }
    if (amountMax) {
      const maxInCents = parseFloat(amountMax) * 100;
      filtered = filtered.filter(t => Math.abs(t.amount) <= maxInCents);
    }

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(t => t.type === typeFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === "date") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortField === "amount") {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    transactions,
    searchQuery,
    statusFilter,
    categoryFilter,
    sortField,
    sortOrder,
    dateRange,
    amountMin,
    amountMax,
    typeFilter,
  ]);

  // Pagination
  const totalPages = Math.ceil(
    filteredAndSortedTransactions.length / itemsPerPage
  );
  const paginatedTransactions = filteredAndSortedTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setFormData({
      companyId: selectedCompanyId || 1,
      date: new Date().toISOString().split("T")[0],
      description: "",
      amount: "",
      currency: "CHF",
      type: "debit",
      category: "",
      accountCode: "",
      accountName: "",
      vatRate: "",
      vatAmount: "",
      reference: "",
      notes: "",
    });
  };

  const handleCreate = () => {
    // Validation
    if (!formData.description || !formData.amount || !formData.accountCode) {
      toast.error("Please fill in all required fields");
      return;
    }

    const amountInCents = Math.round(parseFloat(formData.amount) * 100);
    const vatAmountInCents = formData.vatAmount
      ? Math.round(parseFloat(formData.vatAmount) * 100)
      : undefined;
    const vatRateInBasisPoints = formData.vatRate
      ? Math.round(parseFloat(formData.vatRate) * 100)
      : undefined;

    createMutation.mutate({
      companyId: formData.companyId,
      date: new Date(formData.date).getTime(),
      description: formData.description,
      amount: amountInCents,
      currency: formData.currency,
      type: formData.type,
      category: formData.category,
      accountCode: formData.accountCode,
      accountName: formData.accountName,
      vatRate: vatRateInBasisPoints,
      vatAmount: vatAmountInCents,
      reference: formData.reference || undefined,
      notes: formData.notes || undefined,
      vendorId: formData.vendorId,
      customerId: formData.customerId,
    });
  };

  const handleEdit = () => {
    if (!selectedTransaction) return;

    // Validation
    if (!formData.description || !formData.amount || !formData.accountCode) {
      toast.error("Please fill in all required fields");
      return;
    }

    const amountInCents = Math.round(parseFloat(formData.amount) * 100);
    const vatAmountInCents = formData.vatAmount
      ? Math.round(parseFloat(formData.vatAmount) * 100)
      : undefined;
    const vatRateInBasisPoints = formData.vatRate
      ? Math.round(parseFloat(formData.vatRate) * 100)
      : undefined;

    if (!selectedCompanyId) return;

    updateMutation.mutate({
      id: selectedTransaction.id,
      companyId: selectedCompanyId,
      description: formData.description,
      amount: amountInCents,
      type: formData.type,
      category: formData.category,
      accountCode: formData.accountCode,
      accountName: formData.accountName,
      vatRate: vatRateInBasisPoints,
      vatAmount: vatAmountInCents,
      reference: formData.reference || undefined,
      notes: formData.notes || undefined,
      vendorId: formData.vendorId,
      customerId: formData.customerId,
    });
  };

  const handleDelete = () => {
    if (!selectedTransaction || !selectedCompanyId) return;

    deleteMutation.mutate({
      id: selectedTransaction.id,
      companyId: selectedCompanyId,
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const formatCurrency = (amountInCents: number) => {
    const amount = amountInCents / 100;
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("de-CH");
  };

  // Bulk operations mutations
  const undoBulkDeleteMutation =
    trpc.transactionManagement.undoBulkDelete.useMutation({
      onSuccess: data => {
        toast.success(`Restored ${data.restored} transactions`);
        refetch();
      },
      onError: error => {
        toast.error(`Failed to restore transactions: ${error.message}`);
      },
    });

  const cleanupDeletedMutation =
    trpc.transactionManagement.cleanupDeletedTransactions.useMutation();

  const bulkDeleteMutation =
    trpc.transactionManagement.bulkDeleteTransactions.useMutation({
      onSuccess: data => {
        const idsToDelete = Array.from(selectedIds);
        const { deletedAt } = data;
        let countdown = 10;
        let timerId: NodeJS.Timeout;
        let cleanupTimerId: NodeJS.Timeout;

        setSelectedIds(new Set());
        refetch();

        const toastId = toast.custom(
          t => (
            <div className="bg-card border rounded-lg shadow-lg p-4 flex items-center gap-3">
              <div className="flex-1">
                <div className="font-medium">
                  Deleted {data.deleted} transactions
                </div>
                <div className="text-sm text-muted-foreground">
                  Permanently deleting in {countdown} seconds...
                </div>
              </div>
              <button
                onClick={() => {
                  clearTimeout(timerId);
                  clearTimeout(cleanupTimerId);
                  toast.dismiss(toastId);
                  undoBulkDeleteMutation.mutate({
                    transactionIds: idsToDelete,
                    companyId: selectedCompanyId || 1,
                    deletedAt,
                  });
                }}
                className="px-3 py-1.5 bg-primary text-primary-foreground rounded hover:bg-primary/90 text-sm font-medium min-h-[44px] min-w-[44px]"
              >
                Undo
              </button>
            </div>
          ),
          { duration: 10000 }
        );

        timerId = setInterval(() => {
          countdown--;
          if (countdown > 0) {
            toast.custom(
              t => (
                <div className="bg-card border rounded-lg shadow-lg p-4 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="font-medium">
                      Deleted {data.deleted} transactions
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Permanently deleting in {countdown} seconds...
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      clearTimeout(timerId);
                      clearTimeout(cleanupTimerId);
                      toast.dismiss(toastId);
                      undoBulkDeleteMutation.mutate({
                        transactionIds: idsToDelete,
                        companyId: selectedCompanyId || 1,
                        deletedAt,
                      });
                    }}
                    className="px-3 py-1.5 bg-primary text-primary-foreground rounded hover:bg-primary/90 text-sm font-medium min-h-[44px] min-w-[44px]"
                  >
                    Undo
                  </button>
                </div>
              ),
              { id: toastId, duration: 10000 }
            );
          } else {
            clearInterval(timerId);
          }
        }, 1000);

        cleanupTimerId = setTimeout(() => {
          cleanupDeletedMutation.mutate({
            transactionIds: idsToDelete,
            companyId: selectedCompanyId || 1,
            deletedAt,
          });
          toast.dismiss(toastId);
          toast.success(`Permanently deleted ${data.deleted} transactions`);
          refetch();
        }, 10000);
      },
      onError: error => {
        toast.error(`Failed to delete transactions: ${error.message}`);
      },
    });

  const bulkUpdateCategoryMutation =
    trpc.transactionManagement.bulkUpdateCategories.useMutation({
      onSuccess: data => {
        toast.success(`Updated ${data.updated} transactions`);
        setBulkCategory("");
        setSelectedIds(new Set());
        refetch();
      },
      onError: error => {
        toast.error(`Failed to update categories: ${error.message}`);
      },
    });

  const bulkUpdateStatusMutation =
    trpc.transactionManagement.bulkUpdateStatus.useMutation({
      onSuccess: data => {
        toast.success(`Updated ${data.updated} transactions`);
        setBulkStatus("");
        setSelectedIds(new Set());
        refetch();
      },
      onError: error => {
        toast.error(`Failed to update status: ${error.message}`);
      },
    });

  // Export mutations
  const exportExcelMutation =
    trpc.transactionManagement.exportToExcel.useMutation({
      onSuccess: data => {
        const byteCharacters = atob(data.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.exported(data.filename);
      },
      onError: error => {
        toast.error(`Export failed: ${error.message}`, {
          onRetry: () => handleExportExcel(),
        });
      },
    });

  const handleExportExcel = () => {
    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }
    if (filteredAndSortedTransactions.length === 0) {
      toast.error("No transactions to export");
      return;
    }
    toast.info("Generating Excel file...");
    exportExcelMutation.mutate({
      companyId: selectedCompanyId,
    });
  };

  const handleExportCSV = () => {
    if (!selectedCompanyId) {
      toast.error("Please select a company first");
      return;
    }
    if (filteredAndSortedTransactions.length === 0) {
      toast.error("No transactions to export");
      return;
    }

    const headers = [
      "Date",
      "Description",
      "Amount",
      "Currency",
      "Type",
      "Category",
      "Account Code",
      "Account Name",
      "Reference",
      "Status",
    ];

    const rows = filteredAndSortedTransactions.map(txn => [
      formatDate(txn.date),
      txn.description,
      (txn.amount / 100).toFixed(2),
      txn.currency,
      txn.type,
      txn.category,
      txn.accountCode,
      txn.accountName,
      txn.reference || "",
      txn.status || "pending",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const filename = `transactions_${new Date().toISOString().split("T")[0]}.csv`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.exported(filename);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<
      string,
      "default" | "secondary" | "destructive" | "outline"
    > = {
      pending: "outline",
      processed: "secondary",
      reconciled: "default",
      error: "destructive",
    };
    return (
      <Badge variant={variants[status] || "outline"} className="capitalize">
        {status}
      </Badge>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = getLoginUrl();
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6 pb-8">
        {/* Page Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Transactions</h1>
            <p className="text-muted-foreground mt-1 text-sm md:text-base">
              Manage and categorize your financial transactions
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isMobile ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" className="min-h-[44px]">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setLocation("/bank-import")}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportExcel}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportCSV}>
                    <FileText className="h-4 w-4 mr-2" />
                    Export CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLocation("/bank-import")}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Export to Excel (.xlsx)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <FileText className="h-4 w-4 mr-2" />
                      Export to CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              className="min-h-[44px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              {isMobile ? "New" : "New Transaction"}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {/* Company Selection */}
              <div className="space-y-2">
                <Label className="text-sm">Company</Label>
                <Select value={selectedCompanyId?.toString() || ""} disabled>
                  <SelectTrigger className="min-h-[48px] md:min-h-[36px]">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.length === 0 ? (
                      <SelectItem value="1">Default Company</SelectItem>
                    ) : (
                      companies.map(company => (
                        <SelectItem
                          key={company.id}
                          value={company.id.toString()}
                        >
                          {company.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Search */}
              <div className="space-y-2">
                <Label className="text-sm">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search transactions..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-9 min-h-[48px] md:min-h-[36px]"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label className="text-sm">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="min-h-[48px] md:min-h-[36px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processed">Processed</SelectItem>
                    <SelectItem value="reconciled">Reconciled</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <Label className="text-sm">Category</Label>
                <Select
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                >
                  <SelectTrigger className="min-h-[48px] md:min-h-[36px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="flex items-center justify-between pt-4 border-t mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="min-h-[44px]"
              >
                <Filter className="h-4 w-4 mr-2" />
                {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
              </Button>
              {(dateRange?.from ||
                dateRange?.to ||
                amountMin ||
                amountMax ||
                typeFilter !== "all") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDateRange(undefined);
                    setAmountMin("");
                    setAmountMax("");
                    setTypeFilter("all");
                  }}
                  className="min-h-[44px]"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 pt-4 border-t mt-4">
                {/* Date Range */}
                <div className="space-y-2">
                  <Label className="text-sm">Date Range</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="date"
                      value={
                        dateRange?.from
                          ? new Date(dateRange.from).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={e =>
                        setDateRange({
                          ...dateRange,
                          from: e.target.value
                            ? new Date(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="From"
                      className="min-h-[48px] md:min-h-[36px]"
                    />
                    <Input
                      type="date"
                      value={
                        dateRange?.to
                          ? new Date(dateRange.to).toISOString().split("T")[0]
                          : ""
                      }
                      onChange={e =>
                        setDateRange({
                          ...dateRange,
                          to: e.target.value
                            ? new Date(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="To"
                      className="min-h-[48px] md:min-h-[36px]"
                    />
                  </div>
                </div>

                {/* Amount Range */}
                <div className="space-y-2">
                  <Label className="text-sm">Amount Range (CHF)</Label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={amountMin}
                      onChange={e => setAmountMin(e.target.value)}
                      placeholder="Min"
                      className="min-h-[48px] md:min-h-[36px]"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={amountMax}
                      onChange={e => setAmountMax(e.target.value)}
                      placeholder="Max"
                      className="min-h-[48px] md:min-h-[36px]"
                    />
                  </div>
                </div>

                {/* Transaction Type */}
                <div className="space-y-2">
                  <Label className="text-sm">Transaction Type</Label>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="min-h-[48px] md:min-h-[36px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="debit">Debit</SelectItem>
                      <SelectItem value="credit">Credit</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transactions List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base md:text-lg">
                  Transactions
                </CardTitle>
                <CardDescription>
                  {filteredAndSortedTransactions.length} transaction(s) found
                </CardDescription>
              </div>
              {!isMobile && (
                <ColumnCustomizer
                  storageKey="transactions-table"
                  columns={defaultColumns}
                  defaultVisible={[
                    "date",
                    "description",
                    "account",
                    "category",
                    "amount",
                    "status",
                  ]}
                  value={columns}
                  onChange={setColumns}
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk Actions Toolbar */}
            {selectedIds.size > 0 && (
              <div className="mb-4 p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="font-medium">
                      {selectedIds.size} selected
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      of {filteredAndSortedTransactions.length} transactions
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setSelectedIds(
                          new Set(filteredAndSortedTransactions.map(t => t.id))
                        )
                      }
                      className="text-xs"
                    >
                      Select All
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedIds(new Set())}
                      className="text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Category Update */}
                  <div className="flex gap-2 flex-1">
                    <Select
                      value={bulkCategory}
                      onValueChange={setBulkCategory}
                    >
                      <SelectTrigger className="flex-1 min-h-[44px]">
                        <Tag className="h-4 w-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Set category..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="expense">Expense</SelectItem>
                        <SelectItem value="revenue">Revenue</SelectItem>
                        <SelectItem value="asset">Asset</SelectItem>
                        <SelectItem value="liability">Liability</SelectItem>
                      </SelectContent>
                    </Select>
                    {bulkCategory && (
                      <Button
                        size="sm"
                        onClick={() => {
                          bulkUpdateCategoryMutation.mutate({
                            transactionIds: Array.from(selectedIds),
                            companyId: selectedCompanyId || 1,
                            category: bulkCategory,
                            accountCode: "",
                          });
                        }}
                        disabled={bulkUpdateCategoryMutation.isPending}
                        className="min-h-[44px]"
                      >
                        Apply
                      </Button>
                    )}
                  </div>

                  {/* Status Update */}
                  <div className="flex gap-2 flex-1">
                    <Select value={bulkStatus} onValueChange={setBulkStatus}>
                      <SelectTrigger className="flex-1 min-h-[44px]">
                        <SelectValue placeholder="Set status..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="processed">Processed</SelectItem>
                        <SelectItem value="reconciled">Reconciled</SelectItem>
                      </SelectContent>
                    </Select>
                    {bulkStatus && (
                      <Button
                        size="sm"
                        onClick={() => {
                          bulkUpdateStatusMutation.mutate({
                            transactionIds: Array.from(selectedIds),
                            companyId: selectedCompanyId || 1,
                            status: bulkStatus as
                              | "pending"
                              | "processed"
                              | "reconciled"
                              | "error"
                              | "cancelled",
                          });
                        }}
                        disabled={bulkUpdateStatusMutation.isPending}
                        className="min-h-[44px]"
                      >
                        Apply
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-primary/10">
                  {/* Export Selected */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const selectedTransactions =
                        filteredAndSortedTransactions.filter(t =>
                          selectedIds.has(t.id)
                        );
                      const headers = [
                        "Date",
                        "Description",
                        "Amount",
                        "Currency",
                        "Type",
                        "Category",
                        "Status",
                      ];
                      const rows = selectedTransactions.map(txn => [
                        formatDate(txn.date),
                        txn.description,
                        (txn.amount / 100).toFixed(2),
                        txn.currency,
                        txn.type,
                        txn.category,
                        txn.status || "pending",
                      ]);
                      const csvContent = [
                        headers.join(","),
                        ...rows.map(row =>
                          row.map(cell => `"${cell}"`).join(",")
                        ),
                      ].join("\n");
                      const blob = new Blob([csvContent], {
                        type: "text/csv;charset=utf-8;",
                      });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      const filename = `selected_transactions_${new Date().toISOString().split("T")[0]}.csv`;
                      link.download = filename;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      toast.exported(filename);
                    }}
                    className="min-h-[44px]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Selected
                  </Button>

                  {/* Delete Selected */}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      if (
                        confirm(
                          `Delete ${selectedIds.size} selected transactions? You will have 10 seconds to undo.`
                        )
                      ) {
                        const idsToDelete = Array.from(selectedIds);
                        bulkDeleteMutation.mutate({
                          transactionIds: idsToDelete,
                          companyId: selectedCompanyId || 1,
                        });
                      }
                    }}
                    disabled={bulkDeleteMutation.isPending}
                    className="min-h-[44px]"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Selected
                  </Button>
                </div>
              </div>
            )}

            {/* Mobile Card View */}
            {isMobile ? (
              <div className="space-y-3">
                {isLoadingTransactions ? (
                  <MobileTableCardSkeleton count={5} />
                ) : paginatedTransactions.length === 0 ? (
                  searchQuery || dateRange?.from || statusFilter !== "all" ? (
                    <EmptyFilteredTransactions
                      onClearFilters={() => {
                        setSearchQuery("");
                        setDateRange({});
                        setStatusFilter("all");
                      }}
                    />
                  ) : (
                    <EmptyTransactions
                      onPrimaryAction={() => setIsCreateDialogOpen(true)}
                    />
                  )
                ) : (
                  paginatedTransactions.map(transaction => (
                    <MobileTableCard
                      key={transaction.id}
                      isSelected={selectedIds.has(transaction.id)}
                      onSelect={() => {
                        const newSelected = new Set(selectedIds);
                        if (selectedIds.has(transaction.id)) {
                          newSelected.delete(transaction.id);
                        } else {
                          newSelected.add(transaction.id);
                        }
                        setSelectedIds(newSelected);
                      }}
                    >
                      <MobileTableCardHeader
                        title={transaction.description}
                        subtitle={
                          transaction.reference
                            ? `Ref: ${transaction.reference}`
                            : undefined
                        }
                        badge={getStatusBadge(transaction.status)}
                      />
                      <MobileTableCardRow
                        icon={Calendar}
                        label="Date"
                        value={formatDate(transaction.date)}
                      />
                      <MobileTableCardRow
                        icon={DollarSign}
                        label="Amount"
                        value={
                          <span
                            className={
                              transaction.type === "debit"
                                ? "text-destructive"
                                : "text-success"
                            }
                          >
                            {transaction.type === "debit" ? "-" : "+"}
                            {formatCurrency(transaction.amount)}
                          </span>
                        }
                      />
                      <MobileTableCardRow
                        icon={Tag}
                        label="Category"
                        value={
                          <Badge variant="outline" className="capitalize">
                            {transaction.category}
                          </Badge>
                        }
                      />
                      <MobileTableCardRow
                        icon={Building2}
                        label="Account"
                        value={
                          <div className="text-right">
                            <div className="font-medium">
                              {transaction.accountCode}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {transaction.accountName}
                            </div>
                          </div>
                        }
                      />
                      <MobileTableCardActions>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setIsEditDialogOpen(true);
                          }}
                          className="flex-1 min-h-[44px]"
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setIsDeleteDialogOpen(true);
                          }}
                          className="flex-1 min-h-[44px]"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </MobileTableCardActions>
                    </MobileTableCard>
                  ))
                )}
              </div>
            ) : (
              /* Desktop Table View */
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={
                            selectedIds.size === paginatedTransactions.length &&
                            paginatedTransactions.length > 0
                          }
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedIds(
                                new Set(paginatedTransactions.map(t => t.id))
                              );
                            } else {
                              setSelectedIds(new Set());
                            }
                          }}
                          className="h-4 w-4 cursor-pointer"
                        />
                      </TableHead>
                      {isVisible("date") && (
                        <TableHead
                          className="cursor-pointer select-none"
                          onClick={() => handleSort("date")}
                        >
                          <div className="flex items-center gap-2">
                            Date
                            <ArrowUpDown className="h-4 w-4" />
                          </div>
                        </TableHead>
                      )}
                      {isVisible("description") && (
                        <TableHead
                          className="cursor-pointer select-none"
                          onClick={() => handleSort("description")}
                        >
                          <div className="flex items-center gap-2">
                            Description
                            <ArrowUpDown className="h-4 w-4" />
                          </div>
                        </TableHead>
                      )}
                      {isVisible("account") && <TableHead>Account</TableHead>}
                      {isVisible("category") && (
                        <TableHead
                          className="cursor-pointer select-none"
                          onClick={() => handleSort("category")}
                        >
                          <div className="flex items-center gap-2">
                            Category
                            <ArrowUpDown className="h-4 w-4" />
                          </div>
                        </TableHead>
                      )}
                      {isVisible("type") && <TableHead>Type</TableHead>}
                      {isVisible("reference") && (
                        <TableHead>Reference</TableHead>
                      )}
                      {isVisible("amount") && (
                        <TableHead
                          className="cursor-pointer select-none text-right"
                          onClick={() => handleSort("amount")}
                        >
                          <div className="flex items-center justify-end gap-2">
                            Amount
                            <ArrowUpDown className="h-4 w-4" />
                          </div>
                        </TableHead>
                      )}
                      {isVisible("status") && (
                        <TableHead
                          className="cursor-pointer select-none"
                          onClick={() => handleSort("status")}
                        >
                          <div className="flex items-center gap-2">
                            Status
                            <ArrowUpDown className="h-4 w-4" />
                          </div>
                        </TableHead>
                      )}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingTransactions ? (
                      // Loading skeleton rows
                      Array.from({ length: 10 }).map((_, i) => (
                        <TableRow key={`skeleton-${i}`}>
                          <TableCell>
                            <div className="h-4 w-4 bg-muted animate-pulse rounded" />
                          </TableCell>
                          {isVisible("date") && (
                            <TableCell>
                              <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                            </TableCell>
                          )}
                          {isVisible("description") && (
                            <TableCell>
                              <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                            </TableCell>
                          )}
                          {isVisible("account") && (
                            <TableCell>
                              <div className="h-4 w-28 bg-muted animate-pulse rounded" />
                            </TableCell>
                          )}
                          {isVisible("category") && (
                            <TableCell>
                              <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                            </TableCell>
                          )}
                          {isVisible("amount") && (
                            <TableCell>
                              <div className="h-4 w-24 bg-muted animate-pulse rounded ml-auto" />
                            </TableCell>
                          )}
                          {isVisible("status") && (
                            <TableCell>
                              <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </TableCell>
                          )}
                          {isVisible("reference") && (
                            <TableCell>
                              <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                            </TableCell>
                          )}
                          {isVisible("type") && (
                            <TableCell>
                              <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="h-8 w-8 bg-muted animate-pulse rounded-full ml-auto" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : paginatedTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.filter(c => c.visible).length + 2}
                          className="p-0"
                        >
                          {searchQuery ||
                          dateRange?.from ||
                          statusFilter !== "all" ? (
                            <EmptyFilteredTransactions
                              size="sm"
                              onClearFilters={() => {
                                setSearchQuery("");
                                setDateRange({});
                                setStatusFilter("all");
                              }}
                            />
                          ) : (
                            <EmptyTransactions
                              size="sm"
                              onPrimaryAction={() =>
                                setIsCreateDialogOpen(true)
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedTransactions.map(transaction => (
                        <TableRow
                          key={transaction.id}
                          className={
                            selectedIds.has(transaction.id) ? "bg-muted/50" : ""
                          }
                        >
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(transaction.id)}
                              onChange={e => {
                                const newSelected = new Set(selectedIds);
                                if (e.target.checked) {
                                  newSelected.add(transaction.id);
                                } else {
                                  newSelected.delete(transaction.id);
                                }
                                setSelectedIds(newSelected);
                              }}
                              className="h-4 w-4 cursor-pointer"
                            />
                          </TableCell>
                          {isVisible("date") && (
                            <TableCell className="font-medium">
                              {formatDate(transaction.date)}
                            </TableCell>
                          )}
                          {isVisible("description") && (
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {transaction.description}
                                </div>
                                {!isVisible("reference") &&
                                  transaction.reference && (
                                    <div className="text-sm text-muted-foreground">
                                      Ref: {transaction.reference}
                                    </div>
                                  )}
                              </div>
                            </TableCell>
                          )}
                          {isVisible("account") && (
                            <TableCell>
                              <div className="text-sm">
                                <div className="font-medium">
                                  {transaction.accountCode}
                                </div>
                                <div className="text-muted-foreground">
                                  {transaction.accountName}
                                </div>
                              </div>
                            </TableCell>
                          )}
                          {isVisible("category") && (
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {transaction.category}
                              </Badge>
                            </TableCell>
                          )}
                          {isVisible("type") && (
                            <TableCell>
                              <Badge
                                variant={
                                  transaction.type === "debit"
                                    ? "destructive"
                                    : "default"
                                }
                                className="capitalize"
                              >
                                {transaction.type}
                              </Badge>
                            </TableCell>
                          )}
                          {isVisible("reference") && (
                            <TableCell className="text-sm text-muted-foreground">
                              {transaction.reference || "-"}
                            </TableCell>
                          )}
                          {isVisible("amount") && (
                            <TableCell className="text-right font-medium">
                              <div
                                className={
                                  transaction.type === "debit"
                                    ? "text-destructive"
                                    : "text-success"
                                }
                              >
                                {transaction.type === "debit" ? "-" : "+"}
                                {formatCurrency(transaction.amount)}
                              </div>
                              {transaction.vatAmount && (
                                <div className="text-xs text-muted-foreground">
                                  VAT: {formatCurrency(transaction.vatAmount)}
                                </div>
                              )}
                            </TableCell>
                          )}
                          {isVisible("status") && (
                            <TableCell>
                              {getStatusBadge(transaction.status)}
                            </TableCell>
                          )}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                  setIsEditDialogOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedTransaction(transaction);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="min-h-[44px] min-w-[44px]"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(p => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="min-h-[44px] min-w-[44px]"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Transaction Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Transaction</DialogTitle>
            <DialogDescription>
              Add a new financial transaction to your records
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">
                  Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={e =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">
                  Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: any) =>
                    setFormData({
                      ...formData,
                      type: value,
                      vendorId: undefined,
                      customerId: undefined,
                    })
                  }
                >
                  <SelectTrigger id="type" className="min-h-[48px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit (Expense)</SelectItem>
                    <SelectItem value="credit">Credit (Income)</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vendor selection for expense transactions (debit) */}
            {formData.type === "debit" && (
              <div className="space-y-2">
                <Label htmlFor="vendor">
                  Vendor{" "}
                  <span className="text-muted-foreground">
                    (for VAT tracking)
                  </span>
                </Label>
                <Select
                  value={formData.vendorId?.toString() ?? "__none__"}
                  onValueChange={value =>
                    setFormData({
                      ...formData,
                      vendorId:
                        value && value !== "__none__"
                          ? Number(value)
                          : undefined,
                    })
                  }
                >
                  <SelectTrigger id="vendor" className="min-h-[48px]">
                    <SelectValue placeholder="Select vendor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id.toString()}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Customer selection for revenue transactions (credit) */}
            {formData.type === "credit" && (
              <div className="space-y-2">
                <Label htmlFor="customer">
                  Customer{" "}
                  <span className="text-muted-foreground">
                    (for VAT tracking)
                  </span>
                </Label>
                <Select
                  value={formData.customerId?.toString() ?? "__none__"}
                  onValueChange={value =>
                    setFormData({
                      ...formData,
                      customerId:
                        value && value !== "__none__"
                          ? Number(value)
                          : undefined,
                    })
                  }
                >
                  <SelectTrigger id="customer" className="min-h-[48px]">
                    <SelectValue placeholder="Select customer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {customers.map(customer => (
                      <SelectItem
                        key={customer.id}
                        value={customer.id.toString()}
                      >
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">
                Description <span className="text-destructive">*</span>
              </Label>
              <Input
                id="description"
                placeholder="e.g., Office supplies purchase"
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="min-h-[48px]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">
                  Amount (CHF) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={e =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.category}
                  onValueChange={value =>
                    setFormData({ ...formData, category: value })
                  }
                >
                  <SelectTrigger id="category" className="min-h-[48px]">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="accountCode">
                  Account Code <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="accountCode"
                  placeholder="e.g., 6000"
                  value={formData.accountCode}
                  onChange={e =>
                    setFormData({ ...formData, accountCode: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountName">
                  Account Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="accountName"
                  placeholder="e.g., Operating Expenses"
                  value={formData.accountName}
                  onChange={e =>
                    setFormData({ ...formData, accountName: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vatRate">VAT Rate (%)</Label>
                <Select
                  value={formData.vatRate}
                  onValueChange={value =>
                    setFormData({ ...formData, vatRate: value })
                  }
                >
                  <SelectTrigger id="vatRate" className="min-h-[48px]">
                    <SelectValue placeholder="Select VAT rate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (Exempt)</SelectItem>
                    <SelectItem value="2.5">2.5% (Reduced)</SelectItem>
                    <SelectItem value="3.7">3.7% (Accommodation)</SelectItem>
                    <SelectItem value="7.7">7.7% (Standard)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vatAmount">VAT Amount (CHF)</Label>
                <Input
                  id="vatAmount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.vatAmount}
                  onChange={e =>
                    setFormData({ ...formData, vatAmount: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference">Reference Number</Label>
              <Input
                id="reference"
                placeholder="e.g., INV-2024-001"
                value={formData.reference}
                onChange={e =>
                  setFormData({ ...formData, reference: e.target.value })
                }
                className="min-h-[48px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes about this transaction"
                value={formData.notes}
                onChange={e =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={3}
                className="min-h-[96px]"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
              className="min-h-[48px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createMutation.isPending}
              className="min-h-[48px]"
            >
              {createMutation.isPending ? "Creating..." : "Create Transaction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={open => {
          setIsEditDialogOpen(open);
          if (!open) {
            setSelectedTransaction(null);
            resetForm();
          } else if (selectedTransaction) {
            // Populate form with selected transaction data
            setFormData({
              id: selectedTransaction.id,
              companyId: selectedTransaction.companyId,
              date: new Date(selectedTransaction.date)
                .toISOString()
                .split("T")[0],
              description: selectedTransaction.description,
              amount: (selectedTransaction.amount / 100).toString(),
              currency: selectedTransaction.currency,
              type: selectedTransaction.type,
              category: selectedTransaction.category,
              accountCode: selectedTransaction.accountCode,
              accountName: selectedTransaction.accountName,
              vatRate: selectedTransaction.vatRate
                ? (selectedTransaction.vatRate / 100).toString()
                : "",
              vatAmount: selectedTransaction.vatAmount
                ? (selectedTransaction.vatAmount / 100).toString()
                : "",
              reference: selectedTransaction.reference || "",
              notes: selectedTransaction.notes || "",
              vendorId: selectedTransaction.vendorId,
              customerId: selectedTransaction.customerId,
            });
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Transaction</DialogTitle>
            <DialogDescription>Modify transaction details</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-date">
                  Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={formData.date}
                  onChange={e =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-type">
                  Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: any) =>
                    setFormData({
                      ...formData,
                      type: value,
                      vendorId: undefined,
                      customerId: undefined,
                    })
                  }
                >
                  <SelectTrigger id="edit-type" className="min-h-[48px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit (Expense)</SelectItem>
                    <SelectItem value="credit">Credit (Income)</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vendor selection for expense transactions (debit) */}
            {formData.type === "debit" && (
              <div className="space-y-2">
                <Label htmlFor="edit-vendor">
                  Vendor{" "}
                  <span className="text-muted-foreground">
                    (for VAT tracking)
                  </span>
                </Label>
                <Select
                  value={formData.vendorId?.toString() ?? "__none__"}
                  onValueChange={value =>
                    setFormData({
                      ...formData,
                      vendorId:
                        value && value !== "__none__"
                          ? Number(value)
                          : undefined,
                    })
                  }
                >
                  <SelectTrigger id="edit-vendor" className="min-h-[48px]">
                    <SelectValue placeholder="Select vendor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id.toString()}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Customer selection for revenue transactions (credit) */}
            {formData.type === "credit" && (
              <div className="space-y-2">
                <Label htmlFor="edit-customer">
                  Customer{" "}
                  <span className="text-muted-foreground">
                    (for VAT tracking)
                  </span>
                </Label>
                <Select
                  value={formData.customerId?.toString() ?? "__none__"}
                  onValueChange={value =>
                    setFormData({
                      ...formData,
                      customerId:
                        value && value !== "__none__"
                          ? Number(value)
                          : undefined,
                    })
                  }
                >
                  <SelectTrigger id="edit-customer" className="min-h-[48px]">
                    <SelectValue placeholder="Select customer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {customers.map(customer => (
                      <SelectItem
                        key={customer.id}
                        value={customer.id.toString()}
                      >
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-description">
                Description <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-description"
                placeholder="e.g., Office supplies purchase"
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="min-h-[48px]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-amount">
                  Amount (CHF) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={e =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.category}
                  onValueChange={value =>
                    setFormData({ ...formData, category: value })
                  }
                >
                  <SelectTrigger id="edit-category" className="min-h-[48px]">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-accountCode">
                  Account Code <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-accountCode"
                  placeholder="e.g., 6000"
                  value={formData.accountCode}
                  onChange={e =>
                    setFormData({ ...formData, accountCode: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-accountName">
                  Account Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-accountName"
                  placeholder="e.g., Operating Expenses"
                  value={formData.accountName}
                  onChange={e =>
                    setFormData({ ...formData, accountName: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-vatRate">VAT Rate (%)</Label>
                <Select
                  value={formData.vatRate}
                  onValueChange={value =>
                    setFormData({ ...formData, vatRate: value })
                  }
                >
                  <SelectTrigger id="edit-vatRate" className="min-h-[48px]">
                    <SelectValue placeholder="Select VAT rate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (Exempt)</SelectItem>
                    <SelectItem value="2.5">2.5% (Reduced)</SelectItem>
                    <SelectItem value="3.7">3.7% (Accommodation)</SelectItem>
                    <SelectItem value="7.7">7.7% (Standard)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-vatAmount">VAT Amount (CHF)</Label>
                <Input
                  id="edit-vatAmount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.vatAmount}
                  onChange={e =>
                    setFormData({ ...formData, vatAmount: e.target.value })
                  }
                  className="min-h-[48px]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-reference">Reference Number</Label>
              <Input
                id="edit-reference"
                placeholder="e.g., INV-2024-001"
                value={formData.reference}
                onChange={e =>
                  setFormData({ ...formData, reference: e.target.value })
                }
                className="min-h-[48px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                placeholder="Additional notes about this transaction"
                value={formData.notes}
                onChange={e =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={3}
                className="min-h-[96px]"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setSelectedTransaction(null);
                resetForm();
              }}
              className="min-h-[48px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEdit}
              disabled={updateMutation.isPending}
              className="min-h-[48px]"
            >
              {updateMutation.isPending ? "Updating..." : "Update Transaction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Transaction Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={open => {
          setIsDeleteDialogOpen(open);
          if (!open) {
            setSelectedTransaction(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Transaction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this transaction? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {selectedTransaction && (
            <div className="py-4 space-y-2">
              <div className="p-4 bg-muted rounded-lg">
                <div className="font-medium">
                  {selectedTransaction.description}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {formatDate(selectedTransaction.date)} •{" "}
                  {formatCurrency(selectedTransaction.amount)}
                </div>
                <div className="text-sm text-muted-foreground">
                  {selectedTransaction.accountCode} -{" "}
                  {selectedTransaction.accountName}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSelectedTransaction(null);
              }}
              className="min-h-[48px]"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="min-h-[48px]"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Transaction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
